import type { ReactNode } from "react";

import {
  ComboboxChip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@aircall/ds";

import { MultiSelectTagItemProps } from "./MultiSelectTagItem.decl";

import { TagBeautified } from "@/components/TagBeautified";
import {
  TAG_COLORS,
  THRESHOLD,
} from "@/components/TagBeautified/TagBeautified.constants";
import { MAX_LENGTH } from "@/components/TagList/TagsListItem.constants";
import { getClosestColor } from "@/helpers/colors/colors.helpers";
import { trimWithEllipsis } from "@/helpers/string/string.helpers";
import { useLuminance } from "@/hooks/useLuminance/useLuminance";

/**
 * Both the dropdown row and the selected chip render the tag label inside a
 * fixed pill that clips overflow, so long names get visually cut off. We trim
 * the label to `MAX_LENGTH` everywhere; when it was trimmed, wrap the pill in a
 * tooltip so the full name stays readable (matching the display-mode tag,
 * `TagsListItem`).
 */
function withFullNameTooltip(label: string, pill: ReactNode) {
  if (label.length <= MAX_LENGTH) {
    return pill;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={<span />}>{pill}</TooltipTrigger>
      <TooltipContent side="top" align="center">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Visual body for a tag dropdown row — a colored `TagBeautified` pill.
 */
export function MultiSelectTagItem({ option }: MultiSelectTagItemProps) {
  return withFullNameTooltip(
    option.label,
    <TagBeautified color={option.color}>
      <span className="text-xs font-medium">
        {trimWithEllipsis(option.label, MAX_LENGTH)}
      </span>
    </TagBeautified>
  );
}

/**
 * Selected-tag chip for the Combobox input. Owns the `ComboboxChip` element
 * itself (rather than nesting a `TagBeautified` inside the chip's own pill,
 * which would double-wrap), coloring the chip from the stored tag color and
 * relying on the chip's native remove (X) affordance for deletion.
 */
export function MultiSelectTagChip({ option }: MultiSelectTagItemProps) {
  const { textColor } = useLuminance({
    color: option.color,
    threshold: THRESHOLD,
  });
  const { color: backgroundColor } = getClosestColor(option.color, TAG_COLORS);

  return withFullNameTooltip(
    option.label,
    <ComboboxChip
      data-test="multi-select-tag-chip"
      // See TagBeautified: disable the ds >= 0.21 Badge "film" overlay so the
      // solid tag color isn't washed out.
      style={{ backgroundColor, backgroundImage: "none", color: textColor }}
    >
      <span className="text-xs font-medium">
        {trimWithEllipsis(option.label, MAX_LENGTH)}
      </span>
    </ComboboxChip>
  );
}
